import type { MaybeRefOrGetter } from 'vue'
import { useAuthStore } from '@store/auth'
import { PushChannelPrefix, usePushChannelEvent } from '@/composables/push/pusher'
import type { AuctionLinkPushEventMap, PushEventMap, PushEventRef, PushPayload } from '@/types/push-events'
import { refFiltered } from '@/utils/ref'

export type PushFilter<T> = (event: PushPayload<T>) => boolean
type ChannelKey = MaybeRefOrGetter<string | number | null | undefined>

export function useWorkspacePush<K extends keyof PushEventMap>(
  event: K,
  when?: PushFilter<PushEventMap[K]>,
): PushEventRef<PushEventMap[K]> {
  const authStore = useAuthStore()

  return filtered(usePushChannelEvent(channelName(PushChannelPrefix.Workspace, () => authStore.currentWorkspace?.id), event), when)
}

export function useUserPush<K extends keyof PushEventMap>(
  event: K,
  when?: PushFilter<PushEventMap[K]>,
): PushEventRef<PushEventMap[K]> {
  const authStore = useAuthStore()

  return filtered(usePushChannelEvent(channelName(PushChannelPrefix.User, () => authStore.currentUser?.id), event), when)
}

export function usePortalPush<K extends keyof PushEventMap>(
  otpHash: MaybeRefOrGetter<string | null>,
  event: K,
  when?: PushFilter<PushEventMap[K]>,
): PushEventRef<PushEventMap[K]> {
  return filtered(usePushChannelEvent(channelName(PushChannelPrefix.Portal, otpHash), event), when)
}

export function useAuctionLinkPush<K extends keyof AuctionLinkPushEventMap>(
  auctionHash: MaybeRefOrGetter<string>,
  contactHash: MaybeRefOrGetter<string>,
  event: K,
  when?: PushFilter<AuctionLinkPushEventMap[K]>,
): PushEventRef<AuctionLinkPushEventMap[K]> {
  return filtered(usePushChannelEvent(channelName(PushChannelPrefix.AuctionLink, auctionHash, contactHash), event), when)
}

export function useBookingLinkPush<K extends keyof PushEventMap>(
  linkHash: MaybeRefOrGetter<string>,
  event: K,
  when?: PushFilter<PushEventMap[K]>,
): PushEventRef<PushEventMap[K]> {
  return filtered(usePushChannelEvent(channelName(PushChannelPrefix.BookingLink, linkHash), event), when)
}

export function byId<T extends { id: number }>(id: MaybeRefOrGetter<number>): PushFilter<T> {
  return ({ data }) => data.id === toValue(id)
}

export function byHash<T extends { hash: string }>(hash: MaybeRefOrGetter<string>): PushFilter<T> {
  return ({ data }) => data.hash === toValue(hash)
}

function channelName(prefix: PushChannelPrefix, ...keys: ChannelKey[]): () => string | null {
  return () => {
    const values = keys.map(key => toValue(key))

    return values.every(Boolean) ? `${prefix}${values.join('@')}` : null
  }
}

function filtered<T>(events: PushEventRef<T>, when?: PushFilter<T>): PushEventRef<T> {
  return when ? refFiltered(events, event => !!event && when(event)) : events
}
